import React, { createContext, useContext, useMemo, type ReactElement } from "react";
import DocViewer, {
  DocViewerRenderers,
  type IConfig,
  type IHeaderOverride,
} from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import { usePageTransitionSpinner } from "./utils";

const ToolbarContext = createContext<ReactElement | null>(null);

function ViewerToolbar() {
  const toolbar = useContext(ToolbarContext);
  if (!toolbar) {
    throw new Error("ViewerToolbar must be rendered inside DocumentRenderer.");
  }
  return toolbar;
}

const renderHeader: IHeaderOverride = (state) => {
  if (!state.currentDocument || state.config?.header?.disableFileName) return null;
  return <ViewerToolbar />;
};

// The library resets its current document when config changes, even for header-only updates.
const config: IConfig = {
  header: { overrideComponent: renderHeader },
  csvDelimiter: ",",
  pdfZoom: { defaultZoom: 0.6, zoomJump: 0.5 },
};
const theme = {
  disableThemeScrollbar: false,
  primary: "#4C505C",
  secondary: "#4C505C",
  tertiary: "#4C505C",
};
const style = { height: "100%" };

export default function DocumentRenderer({
  documentUri,
  toolbar,
}: {
  documentUri: string | null | undefined;
  toolbar: ReactElement;
}) {
  const documents = useMemo(() => (documentUri ? [{ uri: documentUri }] : []), [documentUri]);
  usePageTransitionSpinner();

  return (
    <ToolbarContext.Provider value={toolbar}>
      <DocViewer
        style={style}
        pluginRenderers={DocViewerRenderers}
        prefetchMethod="GET"
        config={config}
        theme={theme}
        documents={documents}
      />
    </ToolbarContext.Provider>
  );
}
